import { useCallback, useState, useRef, useMemo } from 'react';
import { Upload, FileText, X, AlertCircle, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  isProcessing?: boolean;
  /** Accepted file extensions e.g. ['.pdf', '.xml']. Defaults to ['.xml']. */
  acceptedExtensions?: string[];
  /** Human label for the format(s), e.g. "PDF" or "XML". Defaults to "XML". */
  formatLabel?: string;
}

export function FileUpload({
  onFilesSelected,
  isProcessing,
  acceptedExtensions = ['.xml'],
  formatLabel = 'XML',
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const exts = useMemo(
    () => acceptedExtensions.map((e) => (e.startsWith('.') ? e : `.${e}`).toLowerCase()),
    [acceptedExtensions]
  );
  const acceptAttr = exts.join(',');
  const extsDisplay = exts.join(', ');

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  }, []);

  const validateFiles = (files: FileList | File[]): File[] => {
    const validFiles: File[] = [];
    const fileArray = Array.from(files);

    fileArray.forEach((file) => {
      const name = file.name.toLowerCase();
      if (exts.some((ext) => name.endsWith(ext))) {
        validFiles.push(file);
      }
    });

    if (validFiles.length === 0 && fileArray.length > 0) {
      setError(`Nenhum arquivo ${formatLabel} encontrado.`);
    } else if (validFiles.length !== fileArray.length) {
      setError(`${validFiles.length} arquivo(s) ${formatLabel} encontrado(s). Outros formatos foram ignorados.`);
    } else {
      setError(null);
    }

    return validFiles;
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }
  }, [exts, formatLabel]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }
    e.target.value = '';
  }, [exts, formatLabel]);

  const handleFolderSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }
    e.target.value = '';
  }, [exts, formatLabel]);

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProcess = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
      setSelectedFiles([]);
    }
  };

  return (
    <div className="space-y-4">
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-8 transition-all duration-200 cursor-pointer',
          'hover:border-primary/50 hover:bg-primary/5',
          isDragging
            ? 'border-primary bg-primary/10 scale-[1.02]'
            : 'border-border bg-card',
          isProcessing && 'opacity-50 pointer-events-none'
        )}
      >
        <input
          type="file"
          accept={acceptAttr}
          multiple
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isProcessing}
        />
        <div className="flex flex-col items-center justify-center text-center">
          <div className={cn(
            'w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors',
            isDragging ? 'bg-primary/20' : 'bg-secondary'
          )}>
            <Upload className={cn(
              'w-8 h-8 transition-colors',
              isDragging ? 'text-primary' : 'text-muted-foreground'
            )} />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            {isDragging ? 'Solte os arquivos aqui' : `Arraste e solte arquivos ${formatLabel}`}
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            ou clique para selecionar
          </p>
          <p className="text-xs text-muted-foreground">
            Suporta múltiplos arquivos • Apenas {extsDisplay}
          </p>
        </div>
      </div>

      {/* Folder selection button */}
      <div className="flex justify-center">
        <input
          ref={folderInputRef}
          type="file"
          // @ts-ignore - webkitdirectory is not in TypeScript types
          webkitdirectory=""
          directory=""
          multiple
          onChange={handleFolderSelect}
          className="hidden"
          disabled={isProcessing}
        />
        <button
          onClick={() => folderInputRef.current?.click()}
          disabled={isProcessing}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            'border border-border hover:bg-secondary hover:border-primary/50',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <FolderOpen className="w-4 h-4" />
          Selecionar Pasta
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">
              Arquivos selecionados ({selectedFiles.length})
            </h4>
            <button
              onClick={handleProcess}
              disabled={isProcessing}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                'bg-primary text-primary-foreground hover:bg-primary/90',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isProcessing ? 'Processando...' : 'Processar Arquivos'}
            </button>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between p-3 bg-secondary rounded-lg animate-fade-in"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="p-1 rounded-full hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
